import React, { useState, useEffect, useMemo, useCallback, } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { updateFamily, fetchFamilyById } from "../../service/FamilyApi";
import { useForm } from "react-hook-form";
import { Helmet } from "react-helmet-async";
import { Navbar, Footer, Loading, Breadcrumbs } from "../../components/shared";
import { useCheckAccess, getCroppedImg, fileToBase64, checkBase64Size, maskAadhar } from "../../utils/Utilities";
import { customGroups } from '../general/DropDownValuesConfig';
import { FileUpload } from '../file/gcp/FileUpload';
import Cropper from 'react-easy-crop';
import '../general/crop.css';

function FamilyUpdate() {
  useCheckAccess('FamilyRW');
  const { id } = useParams();
  const navigate = useNavigate();
  const [familyData, setFamilyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [familyMembersAadhar, setFamilyMembersAadhar] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [error, setError] = useState(null);
  const [croppedImageBase64, setCroppedImageBase64] = useState(null);

  const [showUploadConfirmation, setShowUploadConfirmation] = useState(false);
  const [currentSelectedFile, setCurrentSelectedFile] = useState(null);
  const [uploadMode, setUploadMode] = useState('both'); // 'none', 'both', 'applicationOnly'

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(6);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isAspectRatioLocked, setIsAspectRatioLocked] = useState(true);
  const { register, handleSubmit, formState: { errors, isSubmitting }, trigger, clearErrors, watch, setValue } = useForm();
  useEffect(() => {
    const fetchFamilyData = async () => {
      try {
        if (id) {
          const data = await fetchFamilyById(id);
          setFamilyData(data);
        }
      } catch (error) {
        setError("குடும்பத் தகவலை பெறுவதில் பிழை ஏற்பட்டது");
      }
    };
    setLoading(true);
    fetchFamilyData(); // Call the async function inside useEffect
    setLoading(false);
  }, [id]); // Only re-run if id changes

  // When family data is updated, extract family member Aadhar numbers
  useEffect(() => {
    if (familyData && familyData.family.familyMembers) {
      const aadharNumbers = familyData.family.familyMembers
        .filter(member => member.status !== 'inactive')  // Filter out inactive members
        .map(member => member.aadhar); // Get the aadhar numbers of active members
      setFamilyMembersAadhar(aadharNumbers);  // Set the filtered aadhar numbers

      // Set form default values, ensure id and familyHeadAadhar are set correctly
      setValue("id", String(familyData.family.id));  // Ensure id is set as a string
      setValue("kootamName", familyData.family.kootamName);
      setValue("customGroup", familyData.family.customGroup || "");
      setValue("familyHeadAadhar", familyData.family.familyHeadAadhar);  // Set the familyHeadAadhar correctly
      setValue("status", familyData.family.status);
      setValue("familyName", familyData.family.familyName || "");
    }
  }, [familyData, setValue]);

  const handleChange = async (fieldName) => {
    await trigger(fieldName); // Trigger field validation
    clearErrors(fieldName); // Clear error if valid
  };

  // Validate Aadhar number based on existing family member Aadhar numbers
  const validateAadhar = (value) => {
    if (!familyMembersAadhar.includes(value)) {
      return "ஆதார் எண்ணு குடும்ப உறுப்பினரிடமிருந்து தேர்ந்தெடுக்கப்பட வேண்டும்";
    }
    return true;
  };
  const findUidByAadhar = (aadhar) => {
    const member = familyData.family.familyMembers.find(m => m.aadhar === aadhar);
    return member ? member.uid : null;
  };
  const findMaskedAadhar = (aadhar) => {
    // Assuming 'maskedAadhar' exists in the family members data and it's how you want to validate
    const member = familyData.family.familyMembers.find(m => m.aadhar === aadhar);
    return member ? member.maskedAadhar : null;
  };

  // Handle form submission (API update)
  const onSubmit = async (data) => {
    if (loading) {
      return;  // Prevent form submission if loading is true
    }

    setLoading(true);
    const maskedAadhar = findMaskedAadhar(data.familyHeadAadhar);
    if (
      data.familyHeadAadhar &&
      (maskedAadhar.startsWith("TMP") || maskedAadhar.startsWith("00")) &&
      data.status === "active"
    ) {
      data.status = "draft";
      alert("ஆதார் விவரங்கள் முடிவடையாததால், நிலையை 'active' மாற்ற முடியாது");
      setLoading(false);
      return;
    }


    // Explicitly convert the `id` to string before sending to API
    const familyId = String(familyData.family.id);
    let gcsApplicationFormPath = null;
    let gcsFamilyHeadPhotoPath = null;
    // Only update if something has changed
    if (
      data.kootamName !== familyData.family.kootamName ||
      data.customGroup !== familyData.family.customGroup ||
      data.familyName !== familyData.family.familyName ||
      data.familyHeadAadhar !== familyData.family.familyHeadAadhar ||
      data.status !== familyData.family.status ||
      imageFile
    ) {
      if (imageFile) {
        try {
          const base64Original = await fileToBase64(imageFile);
          if (!checkBase64Size(base64Original)) {
            alert("Application form size should be less than 900 KB.");
            setLoading(false);
            return;
          }

          gcsApplicationFormPath = await FileUpload(
            imageFile,
            'family',
            'application_form',
            familyData.family.uid
          );
          if (gcsApplicationFormPath === false) {
            return;
          }
        } catch (error) {
          console.error("Error uploading original image:", error);
          alert("Error uploading application form:", error);
          setLoading(false);
          return;
        }
      }
      let familyHeadUid = findUidByAadhar(data.familyHeadAadhar);
      // Handle cropped image (family head photo) upload ONLY if uploadMode is 'both' and croppedImage exists
      if (uploadMode === 'both' && croppedAreaPixels) {
        try {
          // Generate the blob from the original imageURL and the croppedAreaPixels
          const blob = await getCroppedImg(imageURL, croppedAreaPixels);

          // Convert blob to Base64 to check its size before proceeding
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          await new Promise(resolve => reader.onloadend = resolve); // Wait for reader to finish
          const base64CroppedForSizeCheck = reader.result;

          if (!checkBase64Size(base64CroppedForSizeCheck)) {
            // Replaced alert
            setErrorMessage("Cropped image size should be less than 900 KB.");
            setLoading(false);
            return;
          }

          // Create a File object from the blob for upload
          const croppedFile = new File([blob], `${familyHeadUid}.jpg`, { type: 'image/jpeg' });
          gcsFamilyHeadPhotoPath = await FileUpload(
            croppedFile,
            'family_member',
            'photo',
            familyHeadUid
          );
          if (gcsFamilyHeadPhotoPath === false) {
            return;
          }
        } catch (error) {
          console.error("Error uploading cropped image:", error);
          alert("Error uploading photo:", error);
          setLoading(false);
          return;
        }
      } else if (uploadMode === 'applicationOnly') {
        // If only application form was chosen, ensure photo path is null
        gcsFamilyHeadPhotoPath = null;
      }


      // Prepare the final data object to send to the API
      const payload = {
        ...data,
        applicationFormPath: gcsApplicationFormPath,
        familyHeadPhotoPath: gcsFamilyHeadPhotoPath,
        familyHeadUid,
        encryptedAadhar: familyData.family.encryptedAadhar
      };


      try {
        const success = await updateFamily(payload, familyId);
        if (success) {
          navigate(`/family/view/${familyId}`);
        }
      } catch (error) {
        console.error("குடும்பத்தை புதுப்பிக்க இயலவில்லை:", error);
      } finally {
        setLoading(false);
      }
    }
  };


  // Memoized URL for the selected image file to be used by ReactCropper
  const imageURL = useMemo(() => {
    if (imageFile) {
      return URL.createObjectURL(imageFile);
    }
    return null;
  }, [imageFile]);
  // Handle file input change for image upload - now triggers confirmation
  const handleImageSelect = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      const MAX_FILE_SIZE = 1024000; // 1 MB
      if (file.size > MAX_FILE_SIZE) {
        alert('Image size should be less than 1 MB.');
        e.target.value = ''; // Clear the file input
        setCurrentSelectedFile(null);
        setImageFile(null); // Clear any previously set imageFile
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null); // Clear any previously cropped image
        setCroppedImageBase64(null); // Clear any previously cropped image Base64
        setUploadMode('none');
      } else {
        setCurrentSelectedFile(file); // Store the selected file temporarily
        setShowUploadConfirmation(true); // Show confirmation dialog
        setErrorMessage('');
      }
    } else {
      // If input is cleared, reset all related states
      setCurrentSelectedFile(null);
      setImageFile(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCroppedImageBase64(null); // Clear any previously cropped image Base64
      setErrorMessage('');
      setUploadMode('none');
      setShowUploadConfirmation(false);
    }
  }, []);

  // Handle user's choice from the confirmation dialog
  const handleConfirmationChoice = useCallback((choice) => {
    if (choice === 'both') {
      setImageFile(currentSelectedFile);
      setUploadMode('both');
    } else if (choice === 'applicationOnly') {
      setImageFile(currentSelectedFile);
      setCroppedAreaPixels(null);
      setCroppedImageBase64(null);
      setUploadMode('applicationOnly');
    }
    setShowUploadConfirmation(false);
    setCurrentSelectedFile(null);
  }, [currentSelectedFile]);

  const handleCancelUpload = useCallback(() => {
    // Reset all image-related states if user cancels
    setCurrentSelectedFile(null);
    setImageFile(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setCroppedImageBase64(null); // Clear any previously cropped image Base64
    setErrorMessage('');
    setUploadMode('none');
    setShowUploadConfirmation(false);
    const fileInput = document.getElementById('applicationFormFile');
    if (fileInput) {
      fileInput.value = '';
    }
  }, []);
  const onCropComplete = useCallback((_, newCroppedAreaPixels) => {
    setCroppedAreaPixels(newCroppedAreaPixels);
  }, []);


  const handleCrop = useCallback(async () => {
    if (!imageURL || !croppedAreaPixels) {
      setErrorMessage('Please select an image and define a crop area first.');
      return;
    }
    if (Object.values(croppedAreaPixels).some(isNaN)) {
      setErrorMessage('Invalid crop area detected. Please try re-cropping.');
      console.error('CroppedAreaPixels contains NaN values:', croppedAreaPixels);
      return;
    }
    try {
      const blob = await getCroppedImg(imageURL, croppedAreaPixels);
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result;
        if (!checkBase64Size(base64data)) {
          setErrorMessage('Cropped image size should be less than 900 KB.');
          setCroppedImageBase64(null);
        } else {
          setCroppedImageBase64(base64data); // Store the Base64 string for preview
          setErrorMessage('');
        }
      };
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        setErrorMessage('Failed to read cropped image data.');
      };
    } catch (error) {
      console.error('Crop failed:', error);
      setErrorMessage('படத்தை வெட்ட முடியவில்லை');
    }
  }, [imageURL, croppedAreaPixels]);


  if (!familyData) {
    return (
      <>
        <Navbar />
        <div className="general_parent">
          <div className="general_container">
            <div className="general_body">
              <Breadcrumbs /><br />
              <Loading />
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  if (error) {
    return <div>Error: {error}</div>; // Display error message
  }
  return (
    <>
      <Navbar />
      <Helmet>
        <title>குடும்பத்தை புதுப்பிக்கவும்</title>
        <link rel="canonical" href={`/family/update/${id}`} />
      </Helmet>

      <div className="general_parent">
        <div className="general_container">
          <div className="general_body">

            <Breadcrumbs
              customLinks={[
                { name: familyData.family.familyName + ' குடும்பம்', path: `/family/view/${id}` }
              ]}
            />
            <br />
            <h3>குடும்ப விவரங்களை திருத்து</h3>
            <br />
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <input
                type="hidden"
                {...register("id")} // Register the id field so it is included in the form data
                value={familyData.family.id}  // Set the value of the id field from the family data
              />
              <div className="details-card">
                <div className="detail-item">
                  <span className="detail-label"><strong>குடும்ப எண் :</strong></span>
                  <span className="detail-value">{familyData.family.id || ""}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label"><strong>தற்போதைய குடும்பத் தலைவர் ஆதார்:</strong></span>
                  <span className="detail-value">{maskAadhar(familyData.family.maskedFamilyHeadAadhar) || ""}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label"><strong>தற்போதைய நிலை:</strong></span>
                  <span className="detail-value">{familyData.family.status || ""}</span>
                </div>

                <div className="detail-item">
                  <span className="detail-label"><strong>குடும்பப் பெயர்:</strong></span>
                  <div className="auth_element">
                    <input
                      type="text"
                      id="familyName"
                      {...register("familyName", {
                        pattern: { value: /^[A-Za-z\u0B80-\u0BFF\s.]*$/, message: "எழுத்துக்கள், இடைவெளிகள் மற்றும் '.' மட்டும் அனுமதிக்கப்படும்" },
                        minLength: { value: 2, message: "குறைந்தது 2 எழுத்துகள் வேண்டும்" },
                      })}
                      className="auth_input"
                      onChange={() => handleChange("familyName")}
                    />
                  </div>
                  {errors.familyName && <div className="auth_error">❗ {errors.familyName.message}</div>}
                </div>

                <div className="detail-item">
                  <span className="detail-label"><strong>கூட்டம்:</strong></span>
                  <div className="auth_element">
                    <select
                      id="kootamName"
                      {...register("kootamName", { required: "கூட்டம் தேர்வு செய்ய வேண்டும்" })}
                      className="auth_select"
                    >
                      <option value="">-- ஒரு கூட்டத்தைத் தேர்ந்தெடுக்கவும் --</option>
                      <option value="செம்பூத்தான்">செம்பூத்தான்</option>
                      <option value="மணியன்">மணியன்</option>
                      <option value="முத்தன்">முத்தன்</option>
                      <option value="பிற">பிற (கூட்டம்)</option>
                    </select>
                    {errors.kootamName && <div className="auth_error">❗ {errors.kootamName.message}</div>}

                    {/* Custom Group Dropdown */}
                    {watch("kootamName") === "பிற" && (
                      <div className="auth_element">
                        <label className="auth_label">பிற (கூட்டம்)</label>
                        <select
                          id="customGroup"
                          {...register("customGroup", {
                            required: "பிற (கூட்டம்) தேவை",
                          })}
                          className="auth_select"
                        >
                          <option value="">-- தேர்ந்தெடுக்கவும் --</option>
                          {customGroups.map((group) => (
                            <option key={group.value} value={group.value}>
                              {group.label}
                            </option>
                          ))}
                        </select>
                        {errors.customGroup && <div className="auth_error">❗ {errors.customGroup.message}</div>}
                      </div>
                    )}

                  </div>
                </div>
                <div className="detail-item">
                  <span className="detail-label"><strong>தற்போதைய பிற குழு:</strong></span>
                  <span className="detail-value">{familyData.family.customGroup || ""}</span>
                </div>

                <div className="detail-item">
                  <span className="detail-label"><strong>குடும்பத் தலைவர் ஆதார்:</strong></span>
                  <div className="auth_element">
                    <select
                      id="familyHeadAadhar"
                      {...register("familyHeadAadhar", {
                        required: "ஆதார் எண்ணு தேவை",
                        validate: validateAadhar
                      })}
                      className="auth_select"
                      value={watch("familyHeadAadhar")}
                    >
                      <option value="">ஆதார் எண்ணைத் தேர்ந்தெடுக்கவும்</option>
                      {familyData.family.familyMembers
                        .filter((member) => /^\d{12}$/.test(member.maskedAadhar)) // Only valid Aadhaar
                        .map((member) => (
                          <option key={member.aadhar} value={member.aadhar}>
                            {member.firstName} {member.lastName} ({maskAadhar(member.maskedAadhar)})
                          </option>
                        ))}
                    </select>
                    {errors.familyHeadAadhar && <div className="auth_error">❗ {errors.familyHeadAadhar.message}</div>}
                  </div>
                </div>

                <div className="detail-item">
                  <span className="detail-label"><strong>நிலை:</strong></span>
                  <div className="auth_radio">
                    <label className="auth_radio-label">
                      <input
                        type="radio"
                        value="active"
                        {...register("status", { required: "நிலை தேவை" })}
                        defaultChecked={familyData.family.status === "active"}
                      />
                      ✅ active (ஏற்றுக்கொள்ளப்பட்டது)
                    </label>
                    <label className="auth_radio-label">
                      <input
                        type="radio"
                        value="inactive"
                        {...register("status", { required: "நிலை தேவை" })}
                        defaultChecked={familyData.family.status === "inactive"}
                      />
                      ❌ inactive (நீக்கப்பட்டது)
                    </label>
                    <label className="auth_radio-label">
                      <input
                        type="radio"
                        value="draft"
                        {...register("status", { required: "நிலை is required" })}
                        defaultChecked={familyData.family.status === "draft"}
                      />
                      📝 draft (குடிபாடு உறுதிப்படுத்தப்படவில்லை)
                    </label>
                    <label className="auth_radio-label">
                      <input
                        type="radio"
                        value="pending"
                        {...register("status", { required: "நிலை is required" })}
                      />
                      ⚠️⏳ pending (Applied by public)
                    </label>
                    {errors.status && <div className="auth_error">❗ {errors.status.message}</div>}
                  </div>
                </div>

                {/* Application Form Upload */}
                <div className="auth_element">
                  <label className="auth_label">விண்ணப்பப் படிவம் (விருப்பத் தேர்வு)</label>
                  <input
                    type="file"
                    id="applicationFormFile"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="auth_input"
                  />
                  {errorMessage && <div className="auth_error">{errorMessage}</div>}

                  {/* Show original image preview */}
                  {imageFile && (
                    <div className="auth_element">
                      <p className="text-gray-700 font-semibold mb-2">🖼️ Original Image Preview:</p>
                      <img
                        src={imageURL}
                        alt="Original Selected"
                        style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ccc', padding: '4px' }}
                      />
                    </div>
                  )}
                </div>


                {/* Confirmation Dialog */}
                {showUploadConfirmation && (
                  <div className="confirmation-dialog">
                    <p >நீங்கள் படங்களை எவ்வாறு பதிவேற்ற விரும்புகிறீர்கள்?</p>
                    <div className="flex justify-around gap-4">
                      <button
                        type="button"
                        onClick={() => handleConfirmationChoice('both')}
                      >
                        விண்ணப்பம் & குடும்பத் தலைவர் புகைப்படம்
                      </button>
                      <button
                        type="button"
                        onClick={() => handleConfirmationChoice('applicationOnly')}
                      >
                        விண்ணப்பம் மட்டும்
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={handleCancelUpload}
                      className="action-button cancel-button"
                    >
                      ரத்து செய்
                    </button>
                  </div>
                )}

                {/* Display Cropper only if image is uploaded AND uploadMode is 'both' */}
                {imageFile && uploadMode === 'both' && (
                  <div className="auth_element" style={{ padding: '16px', border: '1px dashed #ccc', marginBottom: '16px' }}>
                    <p className="text-gray-700 font-semibold mb-2">✂️ Crop Family Head Photo:</p>
                    {/* Aspect Ratio Lock Toggle */}
                    <div style={{ marginBottom: '10px' }}>
                      <input
                        type="checkbox"
                        id="aspectRatioLock"
                        checked={isAspectRatioLocked}
                        onChange={() => setIsAspectRatioLocked(!isAspectRatioLocked)}
                      />
                      <label htmlFor="aspectRatioLock" style={{ marginLeft: '8px' }}>
                        {isAspectRatioLocked ? 'Lock Aspect Ratio (Passport size)' : 'Unlock Aspect Ratio (Free Crop)'}
                      </label>
                    </div>
                    {/* Use the responsive container classes from crop.css */}
                    <div className="cropper-container-wrapper">
                      <div className="cropper-inner-container">
                        {imageURL && (
                          <Cropper
                            image={imageURL}
                            crop={crop}
                            zoom={zoom}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}

                            aspect={isAspectRatioLocked ? 2.5 / 3 : undefined}
                            cropShape="rect"
                            showGrid={true}

                          />
                        )}
                      </div>
                    </div>
                    <div style={{ marginTop: '20px' }}>
                      <label htmlFor="zoom-range" className="block text-sm font-medium text-gray-700">Zoom:</label>
                      <input
                        id="zoom-range"
                        type="range"
                        min={1}
                        max={10}
                        step={0.1}
                        value={zoom}
                        onChange={(e) => setZoom(parseFloat(e.target.value))}
                        className="w-full mb-4"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleCrop}
                      className="action-button"
                    >
                      ✂️ Crop Image
                    </button>
                  </div>
                )}
                {/* Display Cropped Image Preview */}
                {croppedImageBase64 && uploadMode === 'both' && (
                  <>
                    <div className="auth_element">
                      <p className="text-gray-700 font-semibold mb-2">🔍 புகைப்பட முன்னோட்டம்:</p>
                    </div>
                    <img
                      src={croppedImageBase64}
                      alt="புகைப்பட முன்னோட்டம்"
                      style={{ maxWidth: '100%', height: 'auto', objectFit: 'cover', border: '1px solid #ccc', padding: '4px' }}
                    />
                  </>
                )}


                <button type="submit" disabled={isSubmitting || loading}
                  className={`auth_submit ${isSubmitting || loading ? "disabled_btn" : ""}`}
                >
                  {loading ? 'சமர்ப்பிக்கிறது...' : 'சமர்ப்பிக்கவும்'}
                </button>

                <br />
              </div>
            </form>
            <div className="sticky-top-action-bar">
              <button onClick={() => navigate(`/family/member-create/${id}`)}>➕ உறுப்பினர் சேர்</button>
              <button onClick={() => navigate(`/family/view/${id}`)}>👁️ குடும்பத்தை பார்க்க</button>
              <button onClick={() => navigate(`/family/address-update/${id}`)}>🏠 முகவரியை புதுப்பி</button>
              <button onClick={() => navigate(`/family`)}>📁 குடும்ப நிர்வாகம்</button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default FamilyUpdate;